import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import emailService from '../../../services/emailService';

/**
 * Panneau de test pour vérifier la distribution des emails
 */
const EmailTestPanel = () => {
  const [testEmails, setTestEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [error, setError] = useState('');

  // Validation d'email
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Ajouter un email à la liste de test
  const addTestEmail = () => {
    const email = emailInput.trim().toLowerCase();
    
    if (!email) return;
    
    if (!isValidEmail(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    
    if (testEmails.includes(email)) {
      setError('Cette adresse email est déjà dans la liste');
      return;
    }
    
    setTestEmails([...testEmails, email]);
    setEmailInput('');
    setError('');
  };

  // Supprimer un email de la liste
  const removeTestEmail = (email) => {
    setTestEmails(testEmails.filter(e => e !== email));
  };

  // Effacer tous les emails
  const clearTestEmails = () => {
    setTestEmails([]);
    setTestResults(null);
    setError('');
  };

  // Ajouter des emails prédéfinis pour un test rapide
  const addSampleEmails = () => {
    const sampleEmails = [
      'test1@example.com',
      'test2@example.com',
      'test3@example.com'
    ];
    
    const newEmails = sampleEmails.filter(email => !testEmails.includes(email));
    setTestEmails([...testEmails, ...newEmails]);
  };

  // Gérer l'appui sur Entrée
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      addTestEmail();
    }
  };

  // Lancer le test de distribution
  const runDistributionTest = async () => {
    if (testEmails.length === 0) {
      setError('Veuillez ajouter au moins une adresse email pour le test');
      return;
    }

    setLoading(true);
    setError('');
    setTestResults(null);

    try {
      const results = await emailService.testMultipleEmailSend(testEmails);
      setTestResults(results);
    } catch (err) {
      setError(`Erreur lors du test : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Obtenir la couleur selon le statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return '#4CAF50';
      case 'good': return '#8BC34A';
      case 'warning': return '#FF9800';
      case 'critical': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  // Obtenir l'icône selon le statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return <CheckCircleIcon sx={{ color: '#4CAF50' }} />;
      case 'good': return <CheckCircleIcon sx={{ color: '#8BC34A' }} />;
      case 'warning': return <WarningIcon sx={{ color: '#FF9800' }} />;
      case 'critical': return <ErrorIcon sx={{ color: '#F44336' }} />;
      default: return <EmailIcon sx={{ color: '#9E9E9E' }} />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        🧪 Test de distribution des emails
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Testez l'envoi d'emails à plusieurs destinataires pour vérifier que la distribution fonctionne correctement.
      </Typography>

      {/* Configuration des emails de test */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          📧 Adresses email de test
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Entrez une adresse email"
            size="small"
            fullWidth
            disabled={loading}
          />
          <Button
            onClick={addTestEmail}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={loading || !emailInput.trim()}
          >
            Ajouter
          </Button>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            onClick={addSampleEmails}
            variant="outlined"
            size="small"
            disabled={loading}
          >
            Ajouter des exemples
          </Button>
          <Button
            onClick={clearTestEmails}
            variant="outlined"
            size="small"
            disabled={loading || testEmails.length === 0}
          >
            Effacer tout
          </Button>
        </Stack>

        {/* Liste des emails */}
        {testEmails.length > 0 && (
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {testEmails.length} adresse{testEmails.length > 1 ? 's' : ''} de test
            </Typography>
            
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {testEmails.map((email, index) => (
                <Chip
                  key={index}
                  label={email}
                  onDelete={() => removeTestEmail(email)}
                  deleteIcon={<DeleteIcon />}
                  size="small"
                  sx={{ borderRadius: '8px' }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Messages d'erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Bouton de test */}
      <Box sx={{ mb: 4 }}>
        <Button
          onClick={runDistributionTest}
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          disabled={loading || testEmails.length === 0}
          sx={{ px: 4 }}
        >
          {loading ? 'Test en cours...' : `Tester l'envoi (${testEmails.length} destinataires)`}
        </Button>
      </Box>

      {/* Résultats du test */}
      {testResults && (
        <Box>
          <Divider sx={{ mb: 3 }} />
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            📊 Résultats du test
          </Typography>

          {/* Résumé */}
          <Box sx={{ 
            p: 3, 
            backgroundColor: '#f8f9fa', 
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {getStatusIcon(testResults.recommendation.status)}
              <Typography variant="h6" sx={{ 
                fontWeight: 600,
                color: getStatusColor(testResults.recommendation.status)
              }}>
                Taux de réussite : {testResults.summary.successRate}%
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Total</Typography>
                <Typography variant="h6">{testResults.summary.total}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Succès</Typography>
                <Typography variant="h6" sx={{ color: '#4CAF50' }}>{testResults.summary.success}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Échecs</Typography>
                <Typography variant="h6" sx={{ color: '#F44336' }}>{testResults.summary.failure}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Durée moyenne</Typography>
                <Typography variant="h6">{testResults.summary.averageDuration}ms</Typography>
              </Box>
            </Box>

            <LinearProgress 
              variant="determinate" 
              value={testResults.summary.successRate} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getStatusColor(testResults.recommendation.status)
                }
              }}
            />
          </Box>

          {/* Recommandations */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                💡 Recommandations
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {testResults.recommendation.recommendations.map((rec, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                    {rec}
                  </Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Détails par destinataire */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                📋 Détails par destinataire
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Durée</TableCell>
                      <TableCell>Erreur</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.results.map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>{result.order}</TableCell>
                        <TableCell>{result.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={result.success ? 'Succès' : 'Échec'}
                            size="small"
                            color={result.success ? 'success' : 'error'}
                            icon={result.success ? <CheckCircleIcon /> : <ErrorIcon />}
                          />
                        </TableCell>
                        <TableCell>{result.duration}ms</TableCell>
                        <TableCell>
                          {result.error && (
                            <Typography variant="caption" color="error">
                              {result.error}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Box>
  );
};

export default EmailTestPanel; 